import { baseApi, type ApiResponse } from './client';

export interface ManagedAdmin {
  id: number;
  uuid?: string;
  username: string;
  email: string;
  first_name?: string;
  last_name?: string;
  firstName?: string;
  lastName?: string;
  fullName?: string;
  avatar?: string;
  phone_number?: string | null;
  phoneNumber?: string | null;
  roles: string[];
  is_active?: boolean;
  isActive?: boolean;
  is_email_verified?: boolean;
  isEmailVerified?: boolean;
  is_suspended?: boolean;
  isSuspended?: boolean;
  last_login_at?: string | null;
  lastLoginAt?: string | null;
  created_at?: string;
  createdAt?: string;
  updated_at?: string;
  updatedAt?: string;
}

export interface AdminPagination {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  hasNext: boolean;
  hasPrevious: boolean;
}

export interface AdminPaginatedData {
  data: ManagedAdmin[];
  pagination: AdminPagination;
}

export interface AdminListParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  role?: string;
}

export interface CreateManagedAdminRequest {
  username: string;
  email: string;
  password: string;
  first_name: string;
  last_name?: string;
  phone_number?: string;
}

export interface UpdateManagedAdminRequest {
  first_name?: string;
  last_name?: string;
  email?: string;
  phone_number?: string;
  roles?: string[];
  is_active?: boolean;
  is_suspended?: boolean;
  is_email_verified?: boolean;
}

export const adminManagementApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getManagedAdmins: builder.query<ApiResponse<AdminPaginatedData>, AdminListParams | void>({
      query: (params) => ({
        url: '/admin/admin-management/admins',
        method: 'GET',
        params: params || {},
      }),
      providesTags: (result) => {
        const rows = result?.result?.data || [];
        return [
          ...rows.map((admin) => ({ type: 'Admins' as const, id: admin.id })),
          { type: 'Admins' as const, id: 'LIST' },
        ];
      },
    }),
    getManagedAdmin: builder.query<ApiResponse<ManagedAdmin>, string | number>({
      query: (id) => `/admin/admin-management/admins/${id}`,
      providesTags: (_result, _error, id) => [{ type: 'Admins', id }],
    }),
    createManagedAdmin: builder.mutation<ApiResponse<ManagedAdmin>, CreateManagedAdminRequest>({
      query: (body) => ({
        url: '/admin/admin-management/admins',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'Admins', id: 'LIST' }],
    }),
    updateManagedAdmin: builder.mutation<
      ApiResponse<ManagedAdmin>,
      { id: string | number; data: UpdateManagedAdminRequest }
    >({
      query: ({ id, data }) => ({
        url: `/admin/admin-management/admins/${id}`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Admins', id },
        { type: 'Admins', id: 'LIST' },
      ],
    }),
    deleteManagedAdmin: builder.mutation<ApiResponse<void>, string | number>({
      query: (id) => ({
        url: `/admin/admin-management/admins/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'Admins', id: 'LIST' }],
    }),
  }),
});

export const {
  useGetManagedAdminsQuery,
  useGetManagedAdminQuery,
  useCreateManagedAdminMutation,
  useUpdateManagedAdminMutation,
  useDeleteManagedAdminMutation,
} = adminManagementApi;
